import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import type { SprintReportData } from '@sprintpulse/shared';

interface Props {
  teamId: string;
  sprintId: string;
  sprintName: string;
  teamName: string;
  onClose: () => void;
}

type Step = 'configure' | 'loading' | 'review' | 'publishing' | 'success' | 'error';

const DocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14,2 14,8 20,8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const ExternalIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

function Spinner({ color = '#1D4ED8' }: { color?: string }) {
  return (
    <div className="w-7 h-7 rounded-full border-2 animate-spin"
      style={{ borderColor: color, borderTopColor: 'transparent' }} />
  );
}

function StepIndicator({ current }: { current: number }) {
  const steps = ['Configure', 'Review', 'Publish'];
  return (
    <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={label} className="flex items-center gap-1.5">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
              style={{
                background: done ? 'var(--green)' : active ? '#1D4ED8' : 'var(--border)',
                color: done || active ? '#fff' : 'var(--text-3)',
              }}
            >
              {done ? '✓' : idx}
            </div>
            <span className="text-xs" style={{ color: active ? 'var(--text-1)' : 'var(--text-3)', fontWeight: active ? 600 : 400 }}>
              {label}
            </span>
            {i < steps.length - 1 && (
              <div className="w-6 h-px mx-1" style={{ background: 'var(--border)' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ConfluenceReportModal({ teamId, sprintId, sprintName, teamName, onClose }: Props) {
  const defaultTitle = `${teamName} ${sprintName} - Sprint Report`;
  const [step, setStep] = useState<Step>('configure');
  const [parentPageUrl, setParentPageUrl] = useState('');
  const [pageTitle, setPageTitle] = useState(defaultTitle);
  const [sprintGoal, setSprintGoal] = useState('');
  const [reportData, setReportData] = useState<SprintReportData | null>(null);
  const [narrative, setNarrative] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState<{ pageUrl: string; pageTitle: string } | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const stepNumber = step === 'configure' || step === 'loading' ? 1
    : step === 'review' ? 2
    : 3;

  useEffect(() => {
    if (step === 'configure') urlInputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  async function handleLoadPreview() {
    if (!parentPageUrl.trim()) return;
    setStep('loading');
    setErrorMsg('');
    try {
      const data = await api.getConfluencePreview(teamId, sprintId);
      setReportData(data);
      setNarrative(data.aiNarrative);
      if (data.sprintGoal) setSprintGoal(data.sprintGoal);
      setStep('review');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error ?? err?.message ?? 'Failed to load report data');
      setStep('error');
    }
  }

  async function handlePublish() {
    setStep('publishing');
    try {
      const res = await api.publishConfluenceReport(teamId, {
        sprintId,
        parentPageUrl: parentPageUrl.trim(),
        pageTitle: pageTitle.trim() || defaultTitle,
        sprintGoal,
        narrative,
      });
      setResult({ pageUrl: res.pageUrl, pageTitle: res.pageTitle });
      setStep('success');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error ?? err?.message ?? 'Failed to publish report');
      setStep('error');
    }
  }

  const inputStyle = {
    background: 'var(--bg-input, var(--bg))',
    borderColor: 'var(--border)',
    color: 'var(--text-1)',
  };

  const cancelBtn = (
    <button onClick={onClose} className="text-xs px-4 py-2 rounded-md border"
      style={{ color: 'var(--text-2)', borderColor: 'var(--border)', background: 'var(--bg-panel)' }}>
      Cancel
    </button>
  );

  const blueBtn = (label: string, onClick: () => void, disabled = false) => (
    <button onClick={onClick} disabled={disabled}
      className="text-xs px-4 py-2 rounded-md font-medium"
      style={{ background: disabled ? 'var(--border)' : '#1D4ED8', color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl rounded-xl border shadow-xl flex flex-col"
        style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)', maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: '#1D4ED8', color: '#fff' }}>
              <DocIcon />
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>Generate Sprint Report</p>
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>{sprintName}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded flex items-center justify-center text-lg leading-none"
            style={{ color: 'var(--text-3)', background: 'var(--bg-subtle)' }}>×</button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={stepNumber} />

        {/* Scrollable body */}
        <div className="px-5 py-5 overflow-y-auto flex-1">

          {/* ── STEP 1: Configure ── */}
          {(step === 'configure' || step === 'error') && (
            <div className="space-y-4">
              {step === 'error' && (
                <div className="text-xs px-3 py-2.5 rounded-md border"
                  style={{ background: 'var(--red-dim)', borderColor: 'var(--red)', color: 'var(--red)' }}>
                  {errorMsg}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>
                  Confluence Parent Page URL <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input ref={urlInputRef} type="url" value={parentPageUrl}
                  onChange={(e) => setParentPageUrl(e.target.value)}
                  placeholder="https://company.atlassian.net/wiki/spaces/ENG/pages/123456/Sprint+Reports"
                  className="w-full text-xs px-3 py-2 rounded-md border outline-none"
                  style={inputStyle}
                  onKeyDown={(e) => { if (e.key === 'Enter' && parentPageUrl.trim()) handleLoadPreview(); }} />
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                  Paste the URL of the page you want the report nested under
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>Page Title</label>
                <input type="text" value={pageTitle} onChange={(e) => setPageTitle(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-md border outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>
                  Sprint Goal <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional — will be pre-filled from Jira if set)</span>
                </label>
                <input type="text" value={sprintGoal} onChange={(e) => setSprintGoal(e.target.value)}
                  placeholder="e.g. Complete connector upgrade for Phase 1 customers"
                  className="w-full text-xs px-3 py-2 rounded-md border outline-none" style={inputStyle} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                {cancelBtn}
                {blueBtn('Next — Generate Preview →', handleLoadPreview, !parentPageUrl.trim())}
              </div>
            </div>
          )}

          {/* ── LOADING ── */}
          {step === 'loading' && (
            <div className="flex flex-col items-center py-10 gap-4">
              <Spinner />
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--text-1)' }}>Building report…</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>Fetching tickets from Jira and generating AI narrative</p>
              </div>
            </div>
          )}

          {/* ── STEP 2: Review ── */}
          {step === 'review' && reportData && (
            <div className="space-y-5">
              {/* SP Summary */}
              <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-3)' }}>Story Points</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md p-3" style={{ background: 'var(--green-dim)' }}>
                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Completed</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--green)' }}>{reportData.totalSpCompleted} SP</p>
                    {reportData.spDelta !== null && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                        {reportData.spDelta >= 0 ? '+' : ''}{reportData.spDelta} vs {reportData.prevSprintName}
                      </p>
                    )}
                    {(Object.entries(reportData.completedByProject) as [string, number][]).sort((a,b)=>b[1]-a[1]).map(([p, sp]) => (
                      <p key={p} className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>
                        {p}: {sp} SP ({reportData.totalSpCompleted > 0 ? Math.round(sp/reportData.totalSpCompleted*100) : 0}%)
                      </p>
                    ))}
                  </div>
                  <div className="rounded-md p-3" style={{ background: 'var(--red-dim)' }}>
                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>Rolled Over</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--red)' }}>{reportData.rolledSpTotal} SP</p>
                    {reportData.rolledSpDiscount > 0 && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                        {reportData.rolledSpDiscount} SP discountable · net {reportData.rolledSpTotal - reportData.rolledSpDiscount} SP
                      </p>
                    )}
                    {(Object.entries(reportData.incompleteByProject) as [string, number][]).sort((a,b)=>b[1]-a[1]).map(([p, sp]) => (
                      <p key={p} className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>
                        {p}: {sp} SP ({reportData.rolledSpTotal > 0 ? Math.round(sp/reportData.rolledSpTotal*100) : 0}%)
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Ticket counts */}
              <div className="flex gap-3 text-xs">
                {[
                  { label: 'Completed', count: reportData.completedTickets.length, color: 'var(--green)' },
                  { label: 'Rolled Over', count: reportData.rolledTickets.length, color: 'var(--red)' },
                  { label: 'Bugs Fixed', count: reportData.bugsFixed.length, color: 'var(--accent)' },
                ].map(({ label, count, color }) => (
                  <div key={label} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border" style={{ borderColor: 'var(--border)' }}>
                    <span className="font-bold" style={{ color }}>{count}</span>
                    <span style={{ color: 'var(--text-3)' }}>{label}</span>
                  </div>
                ))}
              </div>

              {/* AI Narrative — editable */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <label className="text-xs font-medium" style={{ color: 'var(--text-2)' }}>Comments / Narrative</label>
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: '10px' }}>
                    AI draft — edit before publishing
                  </span>
                </div>
                <textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  rows={8}
                  className="w-full text-xs px-3 py-2.5 rounded-md border outline-none resize-none font-mono leading-relaxed"
                  style={{ background: 'var(--bg-input, var(--bg))', borderColor: 'var(--border)', color: 'var(--text-1)' }}
                />
              </div>

              {/* Sprint goal */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>Sprint Goal</label>
                <input type="text" value={sprintGoal} onChange={(e) => setSprintGoal(e.target.value)}
                  placeholder="e.g. Complete connector upgrade for Phase 1 customers"
                  className="w-full text-xs px-3 py-2 rounded-md border outline-none"
                  style={{ background: 'var(--bg-input, var(--bg))', borderColor: 'var(--border)', color: 'var(--text-1)' }} />
              </div>

              <div className="flex justify-between items-center pt-1">
                <button onClick={() => setStep('configure')} className="text-xs" style={{ color: 'var(--text-3)' }}>
                  ← Back
                </button>
                <div className="flex gap-2">
                  {cancelBtn}
                  {blueBtn('Publish to Confluence →', handlePublish)}
                </div>
              </div>
            </div>
          )}

          {/* ── PUBLISHING ── */}
          {step === 'publishing' && (
            <div className="flex flex-col items-center py-10 gap-4">
              <Spinner />
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--text-1)' }}>Publishing to Confluence…</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>Creating page under your chosen parent</p>
              </div>
            </div>
          )}

          {/* ── SUCCESS ── */}
          {step === 'success' && result && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 px-4 py-3 rounded-md border"
                style={{ background: 'var(--green-dim)', borderColor: 'var(--green)' }}>
                <span style={{ color: 'var(--green)', fontSize: '20px', lineHeight: 1 }}>✓</span>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-1)' }}>Page published successfully</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{result.pageTitle}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="text-xs px-4 py-2 rounded-md border"
                  style={{ color: 'var(--text-2)', borderColor: 'var(--border)', background: 'var(--bg-panel)' }}>
                  Close
                </button>
                <a href={result.pageUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs px-4 py-2 rounded-md font-medium flex items-center gap-1.5"
                  style={{ background: '#1D4ED8', color: '#fff', textDecoration: 'none' }}>
                  Open in Confluence <ExternalIcon />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}